import { useMemo, useState } from 'react'
import { api, query } from '../api/client'
import { useQuery } from '../api/hooks'
import type { Department, Party } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { P } from '../auth/permissions'
import { ErrorBanner, Field, Loading, Modal, Panel, Tag } from '../components/ui'

/**
 * Cost centres — the second of the two analysis dimensions (BUILD_PLAN §3.5).
 *
 * The column has been on the journal line since the first migration and nothing could write it,
 * which made the dimension unassignable rather than unbuilt. A department is only worth naming on
 * an entry because the G&A analysis reads it, so the two arrived together.
 */
export function DepartmentsPage() {
  const { can } = useAuth()
  const [showInactive, setShowInactive] = useState(false)
  const [editing, setEditing] = useState<Department | 'new' | null>(null)

  const departments = useQuery<Department[]>(
    `/departments${query({ includeInactive: showInactive })}`,
    [showInactive],
  )

  const parties = useQuery<Party[]>('/parties')

  const partiesById = useMemo(
    () => new Map((parties.data ?? []).map((p) => [p.id, p])),
    [parties.data],
  )

  async function setActive(department: Department, active: boolean) {
    await api(`/departments/${department.id}/${active ? 'reactivate' : 'deactivate'}`, {
      method: 'POST',
    })
    departments.reload()
  }

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 14 }}>
        <label style={{ textTransform: 'none', fontWeight: 400, fontSize: 13, paddingBottom: 6 }}>
          <input
            type="checkbox"
            style={{ width: 'auto', marginRight: 6 }}
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show closed cost centres
        </label>

        <div className="spacer" />

        {can(P.accounts.create) && (
          <button className="primary" onClick={() => setEditing('new')}>
            + New cost centre
          </button>
        )}
      </div>

      <ErrorBanner error={departments.error} />
      {departments.loading && <Loading />}

      {departments.data?.length === 0 && (
        <div className="banner info">
          <div>
            <strong>No cost centres exist yet.</strong> They are optional: an entry that names none
            still posts, and the G&amp;A analysis reads the whole office as it always has. Create
            some when the office wants to know which side of the practice its overheads belong to —
            litigation, corporate, administration — and code the expense lines to them as they are
            entered.
          </div>
        </div>
      )}

      {departments.data && departments.data.length > 0 && (
        <Panel flush>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Code</th>
                  <th>Name</th>
                  <th style={{ width: 200 }}>العربية</th>
                  <th style={{ width: 200 }}>Manager</th>
                  <th style={{ width: 80 }} className="num">
                    Order
                  </th>
                  <th style={{ width: 100 }}>Status</th>
                  <th style={{ width: 150 }} />
                </tr>
              </thead>
              <tbody>
                {departments.data.map((department) => (
                  <tr key={department.id}>
                    <td className="code">{department.code}</td>
                    <td className="strong">{department.nameEn}</td>
                    <td dir="rtl" className="soft">
                      {department.nameAr}
                    </td>
                    <td>
                      {department.managerPartyId ? (
                        (partiesById.get(department.managerPartyId)?.nameEn ?? (
                          <span className="muted">—</span>
                        ))
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="num soft">{department.displayOrder}</td>
                    <td>
                      {department.isActive ? (
                        <Tag kind="ok">open</Tag>
                      ) : (
                        <Tag kind="default">closed</Tag>
                      )}
                    </td>
                    <td>
                      {can(P.accounts.edit) && department.isActive && (
                        <button className="subtle small" onClick={() => setEditing(department)}>
                          Edit
                        </button>
                      )}
                      {can(P.accounts.deactivate) && (
                        <button
                          className="subtle small"
                          onClick={() => void setActive(department, !department.isActive)}
                        >
                          {department.isActive ? 'Close' : 'Reopen'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {departments.data && departments.data.length > 0 && (
        <div className="muted" style={{ fontSize: 12 }}>
          A cost centre is named on the <strong>income and expense</strong> lines of an entry — a
          bank or a client balance belongs to the office as a whole, and the server refuses one
          there. Closing a cost centre takes it off the pickers and leaves its history on every
          report covering the months it was open.
        </div>
      )}

      {editing && (
        <EditDepartment
          department={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            departments.reload()
          }}
        />
      )}
    </>
  )
}

function EditDepartment({
  department,
  onClose,
  onSaved,
}: {
  department: Department | null
  onClose: () => void
  onSaved: () => void
}) {
  const employees = useQuery<Party[]>(`/parties${query({ type: 'Employee' })}`)

  const [form, setForm] = useState({
    code: department?.code ?? '',
    nameEn: department?.nameEn ?? '',
    nameAr: department?.nameAr ?? '',
    managerPartyId: department?.managerPartyId ?? '',
    displayOrder: String(department?.displayOrder ?? 0),
  })

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>()

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((previous) => ({ ...previous, [key]: value }))
  }

  async function save() {
    setBusy(true)
    setError(undefined)

    try {
      await api(department ? `/departments/${department.id}` : '/departments', {
        method: department ? 'PUT' : 'POST',
        body: {
          code: form.code.trim(),
          nameEn: form.nameEn.trim(),
          nameAr: form.nameAr.trim(),
          managerPartyId: form.managerPartyId || null,
          displayOrder: Number(form.displayOrder) || 0,
        },
      })

      onSaved()
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  const canSave = form.code.trim() !== '' && form.nameEn.trim() !== ''

  return (
    <Modal
      title={department ? `Edit ${department.code}` : 'New cost centre'}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={busy || !canSave} onClick={() => void save()}>
            {busy ? 'Saving…' : department ? 'Save' : 'Create'}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} onDismiss={() => setError(undefined)} />

      <div className="grid cols-2">
        <Field label="Code" hint="Short, and used in exports.">
          <input value={form.code} onChange={(e) => set('code', e.target.value)} placeholder="LIT" />
        </Field>

        <Field label="Order" hint="Where it sits in every picker.">
          <input
            className="num"
            inputMode="numeric"
            value={form.displayOrder}
            onChange={(e) => set('displayOrder', e.target.value)}
          />
        </Field>

        <Field label="Name (English)">
          <input
            value={form.nameEn}
            onChange={(e) => set('nameEn', e.target.value)}
            placeholder="Litigation"
          />
        </Field>

        <Field label="Name (Arabic)">
          <input dir="rtl" value={form.nameAr} onChange={(e) => set('nameAr', e.target.value)} />
        </Field>
      </div>

      <Field label="Manager" hint="Optional. Who answers for this cost centre's spending.">
        <select
          value={form.managerPartyId}
          onChange={(e) => set('managerPartyId', e.target.value)}
        >
          <option value="">—</option>
          {employees.data?.map((employee) => (
            <option key={employee.id} value={employee.id}>
              {employee.nameEn}
            </option>
          ))}
        </select>
      </Field>
    </Modal>
  )
}
